import { ActiveFlow } from "../master";
// -------------------------------
// TERMINATION INTERFACES
// -------------------------------
export interface PendingTermination {
    employeeId?: number | any;
    employeeName?: string;

    terminationType?: string;
    noticeDate?: string;
    terminationDate?: string;
    reason?: string;
}

export interface PendingDeleteTermination {
    awaitingName?: boolean;
}

// -------------------------------
// CREATE TERMINATION — START
// -------------------------------
export const startCreateTerminationFlow = (
    setPendingTermination: (data: PendingTermination | null) => void,
    pushAIChat: (msg: string) => void,
    speak: (msg: string) => void
) => {
    setPendingTermination({});
    pushAIChat("Which employee is being terminated?");
    speak("Which employee is being terminated?");

    window.dispatchEvent(
        new CustomEvent("ai-suggest-options", {
            detail: { type: "employee-termination" }
        })
    );
}; 

// -------------------------------
// CREATE TERMINATION — PROCESS
// -------------------------------
export const processPendingTermination = (
    text: string,
    pending: PendingTermination,
    setPendingTermination: (data: PendingTermination | null) => void,
    setActiveFlow: (flow: ActiveFlow) => void,
    pushAIChat: (msg: string) => void,
    speak: (msg: string) => void,
) => {

    // 2️⃣ Termination Type
    if (!pending.terminationType) {
        const type = text.trim();
        if (!type) {
            pushAIChat("Please provide a valid termination type.");
            speak("Please provide a valid termination type.");
            return;
        }

        setPendingTermination({ ...pending, terminationType: type });
        pushAIChat("What is the notice date?");
        speak("What is the notice date?");
        return;
    }

    // 3️⃣ Notice Date
    if (!pending.noticeDate) {
        const noticeDate = text.trim();
        if (!noticeDate) {
            pushAIChat("Please provide a valid notice date.");
            speak("Please provide a valid notice date.");
            return;
        }

        setPendingTermination({ ...pending, noticeDate });
        pushAIChat("What is the termination date?");
        speak("What is the termination date?");
        return;
    }

    // 4️⃣ Termination Date
    if (!pending.terminationDate) {
        const terminationDate = text.trim();
        if (!terminationDate) {
            pushAIChat("Please provide a valid termination date.");
            speak("Please provide a valid termination date.");
            return;
        }

        setPendingTermination({ ...pending, terminationDate });
        pushAIChat("What is the reason for termination?");
        speak("What is the reason for termination?");
        return;
    }

    // 5️⃣ Reason — FINAL
    if (!pending.reason) {
        const finalPayload = {
            ...pending,
            reason: text.trim()
        };

        pushAIChat("Creating termination now...");
        speak("Creating termination now.");

        window.dispatchEvent(
            new CustomEvent("ai-create-termination", {
                detail: finalPayload
            })
        );

        // ✅ RESET FLOW
        setPendingTermination(null);
        setActiveFlow("none");
        return;
    }
};

// -------------------------------
// DELETE TERMINATION — START
// -------------------------------
export const startDeleteTerminationFlow = (
    setPendingDelete: (data: PendingDeleteTermination | null) => void,
    pushAIChat: (msg: string) => void,
    speak: (msg: string) => void,
    nameFromIntent?: string
) => {
    if (nameFromIntent) {
        pushAIChat(`Deleting termination for "${nameFromIntent}"...`);
        speak(`Deleting termination for ${nameFromIntent}`);

        window.dispatchEvent(
            new CustomEvent("ai-delete-termination", {
                detail: { name: nameFromIntent }
            })
        );
        return;
    }

    setPendingDelete({ awaitingName: true });
    pushAIChat("Which employee’s termination should be deleted?");
    speak("Which employee’s termination should be deleted?");
};

// -------------------------------
// DELETE TERMINATION — PROCESS
// -------------------------------
export const processPendingDeleteTermination = (
    text: string,
    pending: PendingDeleteTermination,
    setPendingDelete: (data: PendingDeleteTermination | null) => void,
    setActiveFlow: (flow: ActiveFlow) => void,
    pushAIChat: (msg: string) => void,
    speak: (msg: string) => void
) => {
    if (!pending.awaitingName) return;

    const cleanName = text.trim();
    if (!cleanName) {
        pushAIChat("Please provide a valid employee name.");
        speak("Please provide a valid employee name.");
        return;
    }

    pushAIChat(`Deleting termination for "${cleanName}"...`);
    speak(`Deleting termination for ${cleanName}`);

    window.dispatchEvent(
        new CustomEvent("ai-delete-termination", {
            detail: { name: cleanName }
        })
    );

    setPendingDelete(null);
    setActiveFlow("none");
};
